import { DownOutlined } from "@ant-design/icons";
import { Menu, Popover } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FromToFilter } from "../api";
import { jsonDeserialize, jsonSerialize, useSetting } from "../util";
import { TimeRangePicker } from "./TimeRangePicker";
import {
  formatLocalizedDateTime,
  formatLocalizedTime,
} from "../util/formatting";

interface PredefinedFromToFilterOption {
  title: string;
  filter: FromToFilter & number;
}

interface CustomFromToFilterOption {
  title: string;
  filter: FromToFilter & { from: Date; to: Date };
}

export type FromToFilterOption =
  | PredefinedFromToFilterOption
  | CustomFromToFilterOption;

const DAY = 60 * 60 * 24;
export const PredefinedFilterOptions: PredefinedFromToFilterOption[] = [
  { title: "beacon:last-messages", filter: 3 * DAY },
  { title: "beacon:last-week", filter: 7 * DAY },
  { title: "beacon:last-day", filter: DAY },
];

export function useTimeRangeSelection(beaconId: string) {
  const { t, i18n } = useTranslation();
  const [customDateFilter, setCustomDateFilter] = useSetting<
    { from: number; to: number } | undefined
  >(
    "beacon:custom-from-to-filter:" + beaconId,
    undefined,
    jsonSerialize,
    jsonDeserialize
  );

  const fromToFilterOptions = useMemo(() => {
    const options: FromToFilterOption[] = PredefinedFilterOptions.map((f) => ({
      title: t(f.title),
      filter: f.filter,
    }));

    if (customDateFilter) {
      const from = new Date(customDateFilter.from);
      const to = new Date(customDateFilter.to);
      const custom: FromToFilterOption = {
        title: t("beacon:last-custom", {
          from: formatLocalizedDateTime(from),
          to:
            to.toLocaleDateString() === from.toLocaleDateString()
              ? formatLocalizedTime(to)
              : formatLocalizedDateTime(to.toLocaleString()),
        }),
        filter: { from, to },
      };
      options.push(custom);
    }
    return options;
  }, [customDateFilter, t, i18n.language]);

  const [selectedFromToFilterOptionIndex, setSelectedFromToFilterOptionIndex] =
    useSetting("beacon:from-to-filter", 2);

  const onSelectCustomTimeFrame = useCallback(
    (from: Date, to: Date) => {
      setCustomDateFilter({ from: from.getTime(), to: to.getTime() });
      setSelectedFromToFilterOptionIndex(PredefinedFilterOptions.length, false);
    },
    [setCustomDateFilter, setSelectedFromToFilterOptionIndex]
  );

  const onSelectFilterOption = useCallback(
    (index: number) => {
      if (typeof fromToFilterOptions[index].filter === "number") {
        setSelectedFromToFilterOptionIndex(index, true);
      } else {
        setSelectedFromToFilterOptionIndex(index, false);
      }
    },
    [setSelectedFromToFilterOptionIndex, fromToFilterOptions]
  );

  const selectedFromToFilter =
    fromToFilterOptions[selectedFromToFilterOptionIndex].filter;

  return {
    selectedFromToFilter,
    fromToFilterOptions,
    selectedFromToFilterOptionIndex,
    onSelectFilterOption,
    onSelectCustomTimeFrame,
  };
}

interface TimeRangeSelectionProps {
  fromToFilterOptions: FromToFilterOption[];
  selectedFromToFilterOptionIndex: number;
  customTimeRangeLabel?: string;
  onSelectFilterOption(index: number): void;
  onSelectCustomTimeFrame(from: Date, to: Date): void;
}

export function TimeRangeSelection(props: TimeRangeSelectionProps) {
  const {
    fromToFilterOptions,
    selectedFromToFilterOptionIndex,
    customTimeRangeLabel,
    onSelectFilterOption,
    onSelectCustomTimeFrame,
  } = props;

  const [open, setOpen] = useState(false);

  const menuItems = [
    ...fromToFilterOptions.map((o, i) => ({
      key: "filter_option_" + i,
      style: { margin: "0px 4px 4px 4px" },
      onClick:
        typeof o.filter === "number"
          ? () => onSelectFilterOption(i)
          : undefined,
      label:
        typeof o.filter === "number" ? (
          o.title
        ) : (
          <TimeRangePicker
            from={o.filter.from}
            to={o.filter.to}
            title={o.title}
            onSelected={onSelectCustomTimeFrame}
          />
        ),
    })),
    ...(fromToFilterOptions.every((o) => typeof o.filter === "number")
      ? [
          {
            key: "custom-filter",
            label: <TimeRangePicker onSelected={onSelectCustomTimeFrame} />,
          },
        ]
      : []),
  ];

  const menu = (
    <Menu selectable={false} onClick={() => setOpen(false)} items={menuItems} />
  );

  return (
    <Popover
      content={menu}
      trigger="hover"
      open={open}
      onOpenChange={setOpen}
      placement="bottom"
    >
      <div
        style={{ fontSize: "14px", color: "#448EF7" }}
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        {selectedFromToFilterOptionIndex === 3 && customTimeRangeLabel
          ? customTimeRangeLabel
          : fromToFilterOptions[selectedFromToFilterOptionIndex].title}{" "}
        {}
        <DownOutlined style={{ fontSize: "14px", color: "#448EF7" }} />
      </div>
    </Popover>
  );
}
